import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { KrabenMark } from '../components/KrabenMark';
import { ExerciseArt } from '../components/ExerciseArt';
import { CardView } from '../components/CardView';
import { RunnerInput, FieldsInput } from './RunnerInput';
import { useToast } from '../components/Toast';
import { getChallenge, deal as dealFrom, promptFor, headlineFor, dayOf, fill, countDeal } from '../data/exercises';
import { renderCard } from '../card/render';
import { cardToPng, warmCardRaster } from '../card/toPng';
import { loadProfile, saveProfile, currentDay, recordMade } from '../lib/state';
import { createPost, fetchPost, type CreatedPost } from '../lib/posts';
import { createChallenge, completeChallenge } from '../lib/challenges';
import { writeUserDoc } from '../lib/feed';
import { shareLink, saveImage, shareText } from '../lib/share';
import { useCountdown, mmss } from '../lib/timer';
import { track, setEventBase, type Door } from '../analytics';
import type { Challenge, Deal, PublicPost, Work } from '../data/types';
import { wordsForCrew } from '../lib/crew-words';
import { CREW, guideFor, GUIDE_LINES } from '../data/crew';
import { Avatar } from '../components/Avatar';
import { Permission } from '../components/Permission';
import { Say } from '../components/Say';
import { dreamPhrase } from '../data/intro-copy';
import { FilmSlotted } from '../components/FilmSlotted';
import { slotsFor, whatItGrew } from '../data/film';
import { partsIn } from '../lib/pieces';
import { FriendPick } from '../components/FriendPick';
import { FRIEND_STORY } from '../components/SendStory';
import { rememberFriendStory } from '../lib/friend-stories';
import { useViewed } from '../lib/viewed';

type Step = 'deal' | 'run' | 'pick' | 'then' | 'kept' | 'slotted';

/**
 * Both doors share this one runner (10 §4, §5). The side door (/do/:id) puts
 * nothing in front of the timer: Begin is the first tap. `?from=postId` is a
 * shared card; the same deal is used and the sender's card is shown above.
 */
export function Exercise() {
  const { challengeId = '' } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const challenge = getChallenge(challengeId);
  const fromId = params.get('from');
  const nav_state = (location.state as { door?: Door; deal?: Deal; fromFilm?: string[]; skipDeal?: boolean; reward?: boolean; copy?: { prompt: string; headline: string; hint: string; placeholder: string; encouragement: string }; warmup?: { seconds: number; blob: Blob | null; storyId?: string }; challengeCode?: string; fromName?: string; myName?: string } | null) || {};
  const copy = nav_state.copy;
  const door: Door = nav_state.door === 'front' ? 'front' : 'side';

  const [profile, setProfile] = useState(loadProfile);
  const day = useMemo(() => currentDay(profile), [profile]);
  const [step, setStep] = useState<Step>('deal');
  useViewed('exercise', step, { exercise: challengeId });
  const [deal, setDeal] = useState<Deal>(() => nav_state.deal || (challenge ? dealFrom(challenge) : {}));
  // Day 2's badge opens with what they said they would like to be on the intro's
  // second screen (owner, 2026-09-18): the exercise is to finish the sentence, not to start cold.
  // "Who are you?" asks for a name and shows the first day's picks back, so both start filled.
  const [work, setWork] = useState<Work>(() => {
    if (challengeId !== 'what-i-am') return {};
    const p = loadProfile();
    const what = dreamPhrase(p.dreams);
    return { ...(p.name ? { name: p.name } : {}), ...(what ? { fields: { what } } : {}) };
  });
  /**
   * The headline, with `{n}`/`{s}` counted off the work as it stands. It takes
   * the work explicitly because the two calls that matter happen inside the
   * keep-and-render path, which is handed the finished `w` rather than reading
   * state that may not have settled yet.
   */
  const headlineNow = (w: Work = work) => {
    const d = { ...deal, ...countDeal(w) };
    return copy ? fill(copy.headline, d) : headlineFor(challenge!, d);
  };
  /**
   * The deal as the post keeps it: without the friend's name. A name typed on
   * the deal screen is for the prompt and the card on this phone, and for the
   * send the person chooses (the challenge link carries it as `toName`); the
   * post is what the crew and the renders could read, and a witness exercise
   * promises no model ever sees the name (14 §11.1, activities audit §3.4).
   */
  const postDeal = (): Deal => {
    const nf = challenge?.input.nameField;
    if (!nf || !(nf in deal)) return deal;
    const { [nf]: _name, ...rest } = deal;
    void _name;
    return rest;
  };
  /** The deal with the friend's name read out, or "a friend" until one is typed. */
  const namedDeal = (): Deal => {
    const nf = challenge!.input.nameField;
    return nf ? { ...deal, [nf]: (deal[nf] || '').trim() || 'a friend' } : deal;
  };
  const promptNow = () => (copy ? fill(copy.prompt, namedDeal()) : promptFor(challenge!, namedDeal()));
  const [startedAt, setStartedAt] = useState<number | null>(null);
  // The clock is opt-in (a cosy default; urgency only when asked for). Time is still measured quietly for the card's footer.
  const [clock, setClock] = useState(false);
  // A raster that failed used to leave the kept screen saying "Pinning it up…"
  // for ever, with every button on it disabled — the card gone and no way to
  // ask for another (interview 2026-09-18, a photograph too big to draw).
  const [renderFailed, setRenderFailed] = useState(false);
  const [progress, setProgress] = useState<{ filled: number; count: number } | null>(null);
  const [from, setFrom] = useState<PublicPost | null>(null);
  const [fromState, setFromState] = useState<'idle' | 'loading' | 'gone'>(fromId ? 'loading' : 'idle');
  const [result, setResult] = useState<{ html: string; png: Blob; pngUrl: string; durationSec: number; endedEarly: boolean } | null>(null);
  const [post, setPost] = useState<CreatedPost | null>(null);
  const [postState, setPostState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const [busy, setBusy] = useState<string | null>(null);
  // given once, on Day 2's name tag (or by the friend who sent you here); never asked for on this screen
  const [myName] = useState(() => loadProfile().name || nav_state.myName || '');
  // The sender named the friend; if the friend has no name yet, that is the one they get (they can change it later).
  useEffect(() => { if (nav_state.myName && !loadProfile().name) { const p = { ...loadProfile(), name: nav_state.myName }; saveProfile(p); setProfile(p); writeUserDoc({ name: nav_state.myName }).catch(() => {}); } }, [nav_state.myName]);
  const [friended, setFriended] = useState<string | null>(null);
  // Where the person was going when the crew's permission moment stopped them (10 §10).
  const [askCrew, setAskCrew] = useState<'feed' | 'reward' | null>(null);
  const isTalk = challenge?.input.kind === 'talk';
  /** Runners that ask their own question and carry their own way on: the talk, and "Who are you?" */
  const isWho = challenge?.input.kind === 'whoami';
  const ownsRun = isTalk || isWho;
  /** A witness exercise's only way out is the send the person chooses, to the friend it is about (14 §11.1). */
  const sendsToFriend = isTalk || !!challenge?.witness;
  /**
   * A Story About You Two, told with the mic on and not a reply: the crew
   * storyboard it, and the send waits on that page (owner, 2026-09-30), not
   * on this card. A reply, or a story with no recording, sends from here.
   */
  const tellsAFriend = challenge?.id === FRIEND_STORY && !nav_state.challengeCode;
  /** The closing step (input.then): one question after the main input, before the card (activities audit §3.2). */
  const hasThen = !!challenge?.input.then?.fields?.length;
  const friendName = challenge?.input.nameField ? deal[challenge.input.nameField] : undefined;
  const { toast, show } = useToast();
  const finishing = useRef(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const lastKeepError = useRef<string>('');
  /** The post being saved in the background, so leaving for the reward can wait for its id. */
  const keeping = useRef<Promise<CreatedPost | null> | null>(null);
  const leaving = useRef(false);

  useEffect(() => {
    if (!challenge) return;
    setEventBase({ challengeId: challenge.id, day, door, path: profile.path });
  }, [challenge, day, door, profile.path]);

  // A shared card: same prompt, the sender's card above it.
  useEffect(() => {
    if (!fromId || !challenge) return;
    let cancelled = false;
    fetchPost(fromId).then((p) => {
      if (cancelled) return;
      if (p && p.challengeId === challenge.id) { setFrom(p); setDeal(p.deal); setFromState('idle'); track('link_opened', { fromPostId: fromId, invite: p.invite }); }
      else setFromState('gone');
    }).catch(() => { if (!cancelled) setFromState('gone'); });
    return () => { cancelled = true; };
  }, [fromId, challenge]);

  const finish = useCallback(async (endedEarly: boolean, w?: Work) => {
    if (!challenge || !startedAt || finishing.current) return;
    const current = w ?? work;
    finishing.current = true;
    const durationSec = Math.round((Date.now() - startedAt) / 1000);
    track(endedEarly ? 'ended_early' : 'completed', { durationSec, clock });
    if (from) track('try_completed', { fromPostId: from.postId });
    const needsPick = challenge.input.pick && (current.lines || []).filter((l) => l.trim()).length > 1 && current.pick === undefined;
    setResult({ html: '', png: new Blob(), pngUrl: '', durationSec, endedEarly });
    const next: Step = needsPick ? 'pick' : hasThen ? 'then' : 'kept';
    setStep(next);
    if (next === 'kept') await render(current, durationSec, endedEarly);
    // Stays set until "Go again" clears it. It used to reset here, so a countdown
    // reaching zero after an early Done finished the same exercise a second time
    // and kept a second post (seen in prod, 2026-09-19).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge, startedAt, work, from, hasThen]);

  const render = async (w: Work, durationSec: number, endedEarly: boolean) => {
    if (!challenge) return;
    setBusy('Pinning it up…');
    try {
      // "Pinning it up" is the one wait in the whole exercise, so in DEV say
      // where the seconds went: the raster, or the round trip.
      const t0 = performance.now();
      const html = renderCard({ challenge, deal, work: w, day, durationSec, endedEarly, headline: copy ? headlineNow(w) : undefined });
      const png = await cardToPng(html);
      const rasterMs = Math.round(performance.now() - t0);
      const pngUrl = URL.createObjectURL(png);
      setResult({ html, png, pngUrl, durationSec, endedEarly });
      track('card_rendered', { shape: challenge.card.shape });
      setProfile(recordMade(profile, day, challenge.id));
      const t1 = performance.now();
      keeping.current = keep({ html, png, durationSec, endedEarly }, w, false);
      void keeping.current.then(() => {
        if (import.meta.env.DEV) console.info(`[kindling] card ${rasterMs}ms to raster (${Math.round(png.size / 1024)}kB), ${Math.round(performance.now() - t1)}ms to keep`);
      });
    } catch (err) {
      console.error('[kindling] card render failed', err);
      track('card_render_failed', { error: String(err).slice(0, 120) });
      setRenderFailed(true);
      show('The card would not render. Your work is still here.');
    } finally { setBusy(null); }
  };

  /** Store the post (PNG + meta) through the backend. Runs in the background; sharing waits on it. */
  const keep = async (r: { html: string; png: Blob; durationSec: number; endedEarly: boolean }, w: Work, invite: boolean): Promise<CreatedPost | null> => {
    if (!challenge) return null;
    setPostState('saving');
    const t0 = performance.now();
    try {
      const created = await createPost({ challengeId: challenge.id, deal: postDeal(), work: w, day, durationSec: r.durationSec, endedEarly: r.endedEarly, headline: invite ? challenge.card.invite || challenge.prompt : headlineNow(w), invite, card: r.png, warmup: invite ? null : nav_state.warmup ?? null });
      if (!invite) { setPost(created); const p = recordMade(loadProfile(), day, challenge.id, created.postId); setProfile(p); }
      // the storyboard of a story about you two may dry after you have left: remember who it is for, on this phone
      if (!invite && challenge.id === FRIEND_STORY && friendName) rememberFriendStory(created.postId, friendName);
      setPostState('saved');
      track('post_kept', { invite, ms: Math.round(performance.now() - t0) });
      if (!invite && nav_state.challengeCode) {
        try { const out = await completeChallenge(nav_state.challengeCode, created.postId, loadProfile().name); setFriended(out.fromName); track('try_completed', { code: nav_state.challengeCode }); } catch (err) { console.warn('[kindling] complete challenge failed', err); }
      }
      return created;
    } catch (err) {
      console.warn('[kindling] keep failed', err);
      lastKeepError.current = (err as Error)?.message || String(err);
      setPostState('failed');
      track('post_keep_failed', { invite, error: lastKeepError.current.slice(0, 120) });
      return null;
    }
  };

  // The card's first raster of a page load is the slow one, so spend it now,
  // on an empty card, while there is nothing to wait for (see warmCardRaster).
  useEffect(() => {
    if (step !== 'run' || !challenge) return;
    void warmCardRaster(renderCard({ challenge, deal, work: {}, day, durationSec: 0, endedEarly: false }));
  }, [step, challenge, deal, day]);

  // The Day 1 session shows the deal on the warm-up's last screen, so the runner opens straight away.
  const autoBegan = useRef(false);
  useEffect(() => {
    if (nav_state.skipDeal && challenge && !autoBegan.current && step === 'deal') {
      autoBegan.current = true;
      const now = Date.now();
      setStartedAt(now);
      setStep('run');
      track('started', { fromPostId: undefined, clock: false, session: 'day-one' });
    }
  }, [nav_state.skipDeal, challenge, step]);

  // The first thing put down, once per run: a runner opened and never touched is
  // a different loss from one abandoned halfway, and `exercise_left` only sees
  // the people who said so.
  const firstInput = useRef<number | null>(null);
  useEffect(() => {
    if (step !== 'run' || !startedAt || firstInput.current === startedAt || !hasWritten(work)) return;
    firstInput.current = startedAt;
    track('first_input', { ms: Date.now() - startedAt });
  }, [step, startedAt, work]);

  const onZero = useCallback(() => { if (clock) void finish(false); }, [finish, clock]);
  const { remaining } = useCountdown((challenge?.minutes ?? 1) * 60, clock ? startedAt : null, onZero);

  if (!challenge) {
    return <Frame><h1>That exercise is not here.</h1><button className="btn ghost" onClick={() => nav('/')}>Today’s exercise</button></Frame>;
  }

  const begin = () => {
    const now = Date.now();
    // A guessing game's dealt subject IS its answer, so it starts life in the
    // secret field: the film stays in front of you while you place the emoji,
    // and picking your own is typing over it (#97).
    if (challenge?.input.secret && !work.secret) {
      const dealt = Object.values(deal).find((v) => v && v.trim());
      if (dealt) setWork({ ...work, secret: dealt });
    }
    finishing.current = false;
    setStartedAt(now);
    setStep('run');
    track('started', { fromPostId: from?.postId, clock });
    if (from) track('try_started', { fromPostId: from.postId });
    saveProfile({ ...profile, draft: { challengeId: challenge.id, deal, work: {}, startedAt: now, day, fromPostId: from?.postId } });
  };

  const onShare = async () => {
    if (!result) return;
    setBusy('Opening the share sheet…');
    try {
      let created = post;
      if (!created) created = await keep(result, work, false);
      if (!created) { show(import.meta.env.DEV ? `Could not save the card: ${lastKeepError.current}` : 'Could not save the card. Try Save image instead.'); return; }
      const outcome = await shareLink(created.shareUrl, shareText(challenge, false));
      track('card_shared', { share_mode: 'share', outcome, platform: 'share' in navigator ? 'share-sheet' : 'clipboard' });
      if (outcome === 'copied') show('Link copied.');
      if (outcome === 'unsupported') show(`Could not open a share sheet here. The link: ${created.shareUrl}`);
    } finally { setBusy(null); }
  };

  /**
   * Keeps the name if one was typed, and is happy with none.
   *
   * A name is what makes a link read as "Sher did this · your turn", so it is
   * worth asking for — but it used to be a gate: no name, no link, and a toast
   * telling you so after you had already pressed Share. A tester asked twice
   * why the app wanted her name in order to send a picture
   * (interview 2026-09-18). Now it is an offer, and a link with no name says
   * "A friend".
   */
  const rememberName = async () => {
    const name = myName.trim().slice(0, 40);
    if (!name) return undefined;
    const p = { ...loadProfile(), name }; saveProfile(p); setProfile(p);
    writeUserDoc({ name }).catch(() => {});
    return name;
  };

  /** The friend's turn: a real challenge link, with this post above it. */
  const onSendToFriend = async () => {
    if (!result) return;
    const name = await rememberName();
    setBusy('Making their link…');
    try {
      let created = post;
      if (!created) created = await keep(result, work, false);
      if (!created) { show('Could not save it. Try again in a moment.'); return; }
      const ch = await createChallenge({ challengeId: challenge.id, deal, fromPostId: created.postId, fromName: name, toName: friendName });
      const text = isTalk ? `I told a story about us${friendName ? `, ${friendName}` : ''}. Your turn: tell it from your side.` : shareText(challenge, true);
      const outcome = await shareLink(ch.shareUrl, text);
      track('challenge_sent', { outcome, to: friendName ? 'named' : 'anyone' });
      if (outcome === 'copied') show('Link copied. Send it to them.');
      if (outcome === 'unsupported') { try { await navigator.clipboard.writeText(ch.shareUrl); show('Link copied.'); } catch { show(ch.url); } }
    } catch (err) { console.warn(err); show((err as { reason?: string }).reason || 'Could not make their link.'); }
    finally { setBusy(null); }
  };

  /** Challenge a friend: the same exercise with the same deal, this post above it (10 §6). */
  const onChallenge = async () => {
    if (!result) return;
    const name = await rememberName();
    setBusy('Making the link…');
    try {
      let created = post;
      if (!created) created = await keep(result, work, false);
      if (!created) { show('Could not save it. Try again in a moment.'); return; }
      // A guessing game carries its answer so the reply to a guess can keep the
      // promise on the card (#96) — and carries no deal at all, because the
      // deal IS the answer and readChallenge hands the deal to the guesser.
      const answer = work.secret?.trim() || undefined;
      const ch = await createChallenge({ challengeId: challenge.id, deal: answer ? {} : deal, fromPostId: created.postId, fromName: name, toName: friendName || undefined, answer });
      const outcome = await shareLink(ch.shareUrl, `${name ? `${name} did` : 'Someone did'} ${challenge.title}. Same prompt, ${challenge.minutes} minutes. Your turn.`);
      track('challenge_sent', { outcome, to: 'anyone' });
      track('card_shared', { share_mode: 'challenge', outcome });
      if (outcome === 'copied') show('Link copied. Send it to them.');
      if (outcome === 'unsupported') { try { await navigator.clipboard.writeText(ch.shareUrl); show('Link copied.'); } catch { show(ch.url); } }
    } catch (err) { console.warn(err); show((err as { reason?: string }).reason || 'Could not make the link. Try again.'); }
    finally { setBusy(null); }
  };

  /** Play it again: the same exercise, a fresh deal, the runner from the top. Nothing about the finished one is lost. */
  const again = () => {
    if (!challenge) return;
    track('again', { challengeId: challenge.id });
    finishing.current = false;
    leaving.current = false;
    keeping.current = null;
    setDeal(dealFrom(challenge, deal));
    setWork({});
    setResult(null);
    setPost(null);
    setPostState('idle');
    setRenderFailed(false);
    setStartedAt(null);
    setProgress(null);
    setStep('deal');
    window.scrollTo(0, 0);
  };

  const onSave = async () => {
    if (!result) return;
    const outcome = await saveImage(result.png, `kindling-${challenge.id}-day-${day}.png`);
    track('card_shared', { share_mode: 'save_image', outcome });
    if (outcome === 'saved') show('Saved.');
  };

  // What was dealt but is not spelled out in the prompt ("the dealt object") gets its own row.
  const guide = challenge ? guideFor(challenge.id) : null;
  const guideHere = !!guide && !!profile.met && guide.id in profile.met;
  const guideRow = (which: 'deal' | 'kept') => guide && guideHere ? (
    <div className="row" style={{ gap: 10 }}><Avatar who={guide.id} size={34} /><div className="stack" style={{ gap: 0 }}><b style={{ fontSize: 14 }}>{guide.name}</b><span className="hint">{GUIDE_LINES[guide.id][which]}</span></div></div>
  ) : null;

  const dealtRow = (() => {
    const shown = Object.entries(deal).filter(([k]) => !challenge.prompt.includes(`{${k}}`) && k !== 'image');
    if (shown.length === 0) return null;
    return (
      <div className="stack" style={{ gap: 6 }}>
        <span className="label">Dealt</span>
        <div className="chips">{shown.map(([k, v]) => <span key={k} className="tag butter" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 15, padding: '8px 14px', whiteSpace: 'normal' }}>{v}</span>)}</div>
      </div>
    );
  })();

  /**
   * Out of an exercise already underway. Picking one is a decision, and until
   * now it was one with no undo: the runner's only controls all went forward,
   * and the pick screen had none at all (interview 2026-09-18). Words on screen
   * are worth one question first; `nav(-1)` returns to whatever opened this —
   * the road, Today, a link — and falls back to Today when nothing did.
   */
  const leaveExercise = () => {
    const written = hasWritten(work);
    if (written && !window.confirm('Leave now? Your changes won’t be saved.')) return;
    track('exercise_left', { challengeId: challenge?.id, step, written });
    if (window.history.length > 1) nav(-1); else nav('/');
  };

  // ── the deal ──────────────────────────────────────────────────────────────
  if (step === 'deal') {
    const seqDay = dayOf(challenge.id, profile.path);
    return (
      <Frame back left={<span className="tag">Day {day}</span>} right={<span className="tag butter">{challenge.minutes} minutes</span>}>
        {from && (
          <div className="stack">
            <CardView pngUrl={from.cardUrl} />
            <p className="why"><b>Someone did {challenge.title}.</b> Same prompt. {challenge.minutes} minutes. No sign-up.</p>
          </div>
        )}
        {fromState === 'gone' && <p className="hint">That exercise is gone, so here is a fresh one.</p>}
        {!from && <div className="row between"><span className="label">{seqDay ? `${seqDay}${seqDay === 1 ? 'st' : seqDay === 2 ? 'nd' : seqDay === 3 ? 'rd' : 'th'} on the road` : 'Off the road'}</span>{!isWho && <KrabenMark size={64} />}</div>}
        {/* "Who are you?" opens on its picture, not a form (owner, 2026-09-29) */}
        {isWho && !from && <ExerciseArt id={challenge.id} className="deal-hero" />}
        {!from && guideRow('deal')}
        <h1>{promptFor(challenge, namedDeal())}</h1>
        {!from && nav_state.fromFilm && nav_state.fromFilm.length > 0 && <span className="hint" style={{ color: 'var(--green-dark)' }}>Dealt from your film.</span>}
        {dealtRow}
        {deal.image && <img src={deal.image} alt="The picture for this exercise" style={{ width: '100%', borderRadius: 14, aspectRatio: '1 / 1', objectFit: 'cover' }} crossOrigin="anonymous" />}
        {!from && Object.keys(challenge.deck).length > 0 && (
          <button className="link" style={{ alignSelf: 'flex-start' }} onClick={() => { const nf = challenge.input.nameField; setDeal({ ...dealFrom(challenge, deal), ...(nf && deal[nf] ? { [nf]: deal[nf] } : {}) }); }}>Something else</button>
        )}
        {/* any exercise about a named person asks for the name here, not only the story (The Terrible Present, How Well Do I Know You) */}
        {challenge.input.nameField && !from && (
          <FriendPick value={deal[challenge.input.nameField] || ''} onChange={(v) => setDeal({ ...deal, [challenge.input.nameField!]: v })}
            label={challenge.input.nameLabel} talk={isTalk} />
        )}
        {challenge.example && (
          <div className="sheet example" style={{ gap: 6, padding: 14 }}>
            <span className="label">Like this</span>
            <div className="example-show">{challenge.example.show}</div>
            <p className="why">{challenge.example.label}</p>
          </div>
        )}
        {challenge.motivation && (
          <div className="sheet" style={{ gap: 6, padding: 14 }}>
            <span className="label">Why this exercise</span>
            <p className="why">{challenge.motivation}</p>
          </div>
        )}
        <div className="stack" style={{ marginTop: 'auto' }}>
          {/* the last thing read before the clock could start, because what it
              says is "go and fetch something" (interview 2026-09-18) */}
          {challenge.materials && (
            <div className="materials">
              <span className="label">Before you start</span>
              <p>{challenge.materials}</p>
            </div>
          )}
          {!ownsRun && <label className={`clock-opt ${clock ? 'on' : ''}`}>
            <input type="checkbox" checked={clock} onChange={(e) => setClock(e.target.checked)} />
            <span className="clock-box" aria-hidden="true">{clock ? '✓' : ''}</span>
            <span>Put me on the clock · {challenge.minutes} min</span>
          </label>}
          {isTalk ? (() => {
            // The mic itself, not a button saying "Tap the mic" (owner, 2026-09-30):
            // one tap and it is recording, as on Day 1's warm-up.
            const needsName = !!challenge.input.nameField && !(deal[challenge.input.nameField] || '').trim();
            return (
              <div className="deal-mic">
                <button className="mic-btn" onClick={begin} disabled={fromState === 'loading' || needsName} aria-label="Record">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" stroke="none" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /><path d="M9 21h6" /></svg>
                </button>
                <span className="counter rec-label">{needsName ? 'Their name first' : <><span className="rec-dot" />Tap to record</>}</span>
              </div>
            );
          })() : <button className="btn coral" onClick={begin} disabled={fromState === 'loading'}>{fromState === 'loading' ? 'One moment' : 'Begin'}</button>}
          <p className="hint" style={{ textAlign: 'center' }}>About {challenge.minutes} minutes. Done whenever you like. Nobody sees it unless you send it.</p>
        </div>
        {toast}
      </Frame>
    );
  }

  // ── the runner ────────────────────────────────────────────────────────────
  if (step === 'run') {
    return (
      <Frame onBack={leaveExercise} left={<span className="tag">Day {day} · {challenge.title}</span>}
        right={clock ? <span className={`timer ${remaining < 30 ? 'low' : ''}`}>{mmss(remaining)}</span> : progress ? <span className="tag">{progress.filled} of {progress.count}</span> : undefined}>
        {!ownsRun && <h2 style={{ fontSize: 24 }}>{promptNow()}</h2>}
        {!ownsRun && dealtRow}
        {deal.image && <img src={deal.image} alt="The picture for this exercise" style={{ width: '100%', borderRadius: 14, maxHeight: 220, objectFit: 'cover' }} crossOrigin="anonymous" />}
        <RunnerInput challenge={challenge} work={work} onChange={setWork} onPhotoBusy={setPhotoBusy} onProgress={setProgress} copy={copy ? { placeholder: copy.placeholder, hint: copy.hint } : undefined} subject={friendName} onFinish={(w) => finish(false, w)} />
        {!ownsRun && (
          <div className="row between" style={{ marginTop: 'auto', paddingTop: 8 }}>
            <button className="link" onClick={() => { track('cancelled'); nav('/'); }}>Cancel</button>
            <button className="btn small" onClick={() => finish(clock ? remaining > 0 : !!progress && progress.filled < progress.count)} disabled={!!busy || photoBusy}>{busy || (photoBusy ? 'Adding your photo…' : progress && progress.filled < progress.count ? 'Done for now' : 'Done')}</button>
          </div>
        )}
        {toast}
      </Frame>
    );
  }

  // ── the pick (poster line, funniest caption, the invented one) ──────────────
  if (step === 'pick' && result) {
    const lines = (work.lines || []).map((l, i) => ({ l: l.trim(), i })).filter((x) => x.l);
    return (
      <Frame onBack={leaveExercise} left={<span className="tag">Day {day}</span>}>
        <h1>{challenge.input.pickLabel}</h1>
        {challenge.input.pickHidden && <p className="hint">Only you see this.</p>}
        <div className="stack">
          {lines.map(({ l, i }) => (
            <button key={i} className={`choice ${work.pick === i ? 'on' : ''}`} onClick={() => setWork({ ...work, pick: i })}>
              <span className="n">{i + 1}</span><span style={{ textAlign: 'left' }}>{l}</span>
            </button>
          ))}
        </div>
        <button className="btn" disabled={work.pick === undefined || !!busy} onClick={async () => { if (hasThen) { setStep('then'); return; } setStep('kept'); await render(work, result.durationSec, result.endedEarly); }}>{busy || 'Keep it'}</button>
        {toast}
      </Frame>
    );
  }

  // ── the closing step: one more question, in your own words ─────────────────
  // After the list or the photograph, before the card: the question that makes
  // it a piece of the film ("Who does that? Give them a name."). Never a gate:
  // Finish works with it blank, and a blank one simply feeds nothing.
  if (step === 'then' && result && challenge.input.then) {
    const spec = challenge.input.then;
    const picked = work.pick !== undefined ? work.lines?.[work.pick]?.trim() : undefined;
    const answered = spec.fields.some((f) => (work.fields?.[f.key] || '').trim());
    return (
      <Frame onBack={leaveExercise} left={<span className="tag">Day {day} · {challenge.title}</span>}>
        {spec.label && <h1>{spec.label}</h1>}
        {picked && !challenge.input.pickHidden && <div className="idea-card">“{picked}”</div>}
        <div className="stack">
          <FieldsInput fields={spec.fields} work={work} onChange={setWork} />
          <span className="hint">One line is plenty. It goes into your film in your words.</span>
        </div>
        <button className="btn" style={{ marginTop: 'auto' }} disabled={!!busy} onClick={async () => { setStep('kept'); await render(work, result.durationSec, result.endedEarly); }}>{busy || (answered ? 'Finish' : 'Finish without it')}</button>
        {toast}
      </Frame>
    );
  }

  // ── kept: finished, and yours ─────────────────────────────────────────────
  /** Off the card, to the feed or to the Day 1 reward. */
  const leave = async (to: 'feed' | 'reward') => {
    if (to === 'feed') return nav('/feed');
    // The reward watches the post for the crew's picture, so it needs the post's
    // id. Next lights up once the card is drawn, which can be before the post
    // has finished saving — longer with "Who are you?", whose pictures go with
    // it — and a reward opened without an id decided nothing was coming and
    // never showed the picture that did (seen 2026-09-30).
    if (leaving.current) return;
    leaving.current = true;
    let saved = post;
    if (!saved && keeping.current) { setBusy('One moment'); saved = await keeping.current; setBusy(null); }
    nav('/next', { state: { challengeId: challenge.id, headline: headlineNow(), pngUrl: result?.pngUrl, day, postId: saved?.postId, shareUrl: saved?.shareUrl, subject: deal.situation, ideas: wordsForCrew(work), parts: partsIn(challenge, work), storyId: tellsAFriend ? saved?.postId : nav_state.warmup?.storyId, ...(tellsAFriend ? { sendTo: friendName } : {}) }, viewTransition: true });
  };
  /**
   * The card is the payoff, so it is never shared with anything. Permission
   * comes after it, on its own screen, on the way to the feed — which is the
   * last moment before the crew could do anything with what was just made.
   */
  const putItOnYourFeed = () => {
    // Every card goes to the crew's darkroom and the print while the crew are on
    // (owner, 2026-09-27: "the darkroom needs to show up after each activity");
    // the picture's flight into its slot there is the film moment. With the crew
    // off, the card drops into the film on its own screen (owner, 2026-09-21).
    const crewOn = (loadProfile().crew || 'unasked') !== 'off';
    if (step === 'kept' && slotsFor(challenge).length && !friended && !nav_state.reward && !crewOn) { setStep('slotted'); return; }
    const to = nav_state.reward || (crewOn && !friended) ? 'reward' : 'feed';
    if ((loadProfile().crew || 'unasked') === 'unasked') { setAskCrew(to); return; }
    leave(to);
  };

  if (askCrew) return <Permission day={day} onAnswer={() => { const to = askCrew; setAskCrew(null); leave(to); }} />;

  if (step === 'slotted') return <FilmSlotted challenge={challenge} day={day} parts={partsIn(challenge, work)} postId={post?.postId} onNext={putItOnYourFeed} />;

  const isPrivate = !!challenge.input.private;
  // Day 1's share comes after the picture your crew makes (the reward), not here.
  const shareLater = !!nav_state.reward;
  const goAgainLabel = isPrivate ? 'Another page' : Object.keys(challenge.deck).length ? 'Go again · fresh deal' : 'Go again';

  // When the crew's print comes next, that is the payoff and it shows what you
  // wrote; reading the list back here only confused people, who had just seen it
  // on the pick (owner, 2026-09-28). This screen is the lesson and nothing else.
  // The card still renders behind it: the reward and the feed need it.
  const crewOn = (loadProfile().crew || 'unasked') !== 'off';
  const storyboarding = tellsAFriend && !!work.audio && crewOn;
  if (!isPrivate && !friended && (!sendsToFriend || storyboarding) && !nav_state.challengeCode && (nav_state.reward || crewOn)) {
    const speaker = guide && guideHere ? guide : CREW.kraben;
    const line = whatItGrew(challenge).brief || GUIDE_LINES[speaker.id].kept;
    return (
      <Frame back left={<span className="tag">Day {day} · {challenge.title}</span>}>
        <div className="lesson">
          <p className="lesson-bubble">{line}</p>
          <Avatar who={speaker.id} size={196} />
          <div className="lesson-who"><b>{speaker.name}</b><span className="hint">{speaker.part}</span></div>
        </div>
        {storyboarding && <p className="why" style={{ textAlign: 'center' }}>Your crew are storyboarding it now. When it comes out, it’s yours to send to {friendName || 'them'}.</p>}
        <div className="stack">
          {renderFailed && !result?.pngUrl && (
            <button className="btn" disabled={!!busy} onClick={() => { setRenderFailed(false); void render(work, result?.durationSec ?? 0, result?.endedEarly ?? false); }}>{busy || 'Try again'}</button>
          )}
          <button className="btn" onClick={putItOnYourFeed} disabled={!result?.pngUrl}>{result?.pngUrl ? 'Next' : busy || 'One moment'}</button>
          <div className="row" style={{ justifyContent: 'center', gap: 18 }}>
            <button className="link" onClick={again} disabled={!result?.pngUrl && !renderFailed}>{goAgainLabel}</button>
            <button className="link" onClick={() => nav('/path')}>The road</button>
          </div>
          <Say where="kept" day={day} challengeId={challenge.id} />
        </div>
        {toast}
      </Frame>
    );
  }

  return (
    <Frame back left={<span className="tag">Day {day} · {challenge.title}</span>}>
      {/* No headline over the card (owner, 2026-09-21): the card is the thing; a title only repeated it. */}
      {friended ? <h1>You and {friended} are friends now.</h1> : <span className="label" style={{ color: 'var(--green-dark)' }}>{day === 1 && nav_state.reward ? 'Day 1 · the first thing you made here' : `Day ${day} · made`}</span>}
      {friended && <p className="why">Your story and theirs are now on both your feeds.</p>}
      {guideRow('kept')}
      {isTalk && work.audio && <audio className="player" controls src={URL.createObjectURL(work.audio)} />}
      {result?.pngUrl ? <CardView pngUrl={result.pngUrl} sheet /> : <div className="card-preview" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 18 }}><span className="counter">{busy || (renderFailed ? 'The card would not come out. What you wrote is still here.' : 'Pinning it up…')}</span></div>}
      <div className="row" style={{ justifyContent: 'center' }}>
        <span className="dot" /><span className="counter">Day {day} of making things</span>
        <span className="hint">· {result?.endedEarly ? 'Finished early.' : 'Finished.'}</span>
      </div>
      <div className="stack">
        {sendsToFriend && !friended && !nav_state.challengeCode && (
          <div className="sheet" style={{ gap: 8 }}>
            <span className="label">Send it to {friendName || 'them'}</span>
            {/* your name is never asked here: it was given on Day 2's name tag, and a link without one says "A friend" (owner, 2026-09-30) */}
            <p className="why">They’ll {isTalk ? 'hear you tell it, then tell it back from their side' : 'see it, then it’s their turn'}. Plus you’ll be friends on here!</p>
            <button className="btn coral" onClick={onSendToFriend} disabled={!result?.pngUrl || !!busy || postState === 'saving'}>{busy || `Send it to ${friendName || 'them'}`}</button>
          </div>
        )}
        {isPrivate && <p className="why" style={{ textAlign: 'center' }}>Done. This one will stay private.</p>}
        {!isPrivate && !friended && <WhatThatDid challenge={challenge} />}
        {renderFailed && !result?.pngUrl && (
          <button className="btn" disabled={!!busy} onClick={() => { setRenderFailed(false); void render(work, result?.durationSec ?? 0, result?.endedEarly ?? false); }}>{busy || 'Try again'}</button>
        )}
        <button className="btn" onClick={putItOnYourFeed} disabled={!result?.pngUrl}>Next</button>
        {!isPrivate && !shareLater && <div className="row" style={{ gap: 10 }}>
          <button className="btn small butter" style={{ flex: 1 }} onClick={onShare} disabled={!result?.pngUrl || !!busy}>Share it</button>
          {!sendsToFriend && !nav_state.challengeCode && <button className="btn small coral" style={{ flex: 1 }} onClick={onChallenge} disabled={!result?.pngUrl || !!busy}>Challenge a friend</button>}
        </div>}
        <div className="row" style={{ justifyContent: 'center', gap: 18 }}>
          {!isPrivate && !shareLater && <button className="link" onClick={onSave} disabled={!result?.pngUrl}>Save image</button>}
          {!nav_state.challengeCode && <button className="link" onClick={again} disabled={!result?.pngUrl && !renderFailed}>{goAgainLabel}</button>}
          <button className="link" onClick={() => nav('/path')}>The road</button>
        </div>
        {postState === 'failed' && <p className="hint" style={{ textAlign: 'center' }}>Could not reach the shelf. Try again later.</p>}
        {/* Right after the card, which is the moment somebody has just felt something about it. */}
        <Say where="kept" day={day} challengeId={challenge.id} />
      </div>
      {toast}
    </Frame>
  );
}

/**
 * Straight after the card: what that exercise grew, in one sentence. A
 * professional writer (interview 3, 2026-09-20) finished an exercise and felt
 * lost; she wanted to know, right then, how it had made her more creative.
 * Brief on purpose (owner, 2026-09-21): the film screen after it has room for
 * the rest, and an exercise that feeds no slot gets the whole reason here.
 */
/** Anything at all put down in the runner: a word, a photo, an emoji, a recording. */
function hasWritten(work: Work): boolean {
  return !!(work.lines?.some((l) => l.trim()) || work.text?.trim() || work.photo || work.emoji?.length
    || work.pairs?.some((p) => p.name.trim() || p.sub.trim()) || Object.values(work.fields ?? {}).some((v) => v.trim()) || work.audio || work.made?.length || work.selfie);
}

function WhatThatDid({ challenge }: { challenge: Challenge }) {
  const { brief, more } = whatItGrew(challenge);
  if (!brief) return null;
  const whole = !slotsFor(challenge).length;
  return (
    <div className="grew">
      <span className="label" style={{ color: 'var(--green-dark)' }}>What that just grew</span>
      <p className="why">{whole && more ? `${brief} ${more}` : brief}</p>
    </div>
  );
}

